import { useLostFoundPage } from "@/hooks/useLostFoundPage";
import LostFoundHeader from "@/components/lost-found/LostFoundHeader";
import LostFoundBody from "@/components/lost-found/LostFoundBody";
import LostFoundModals from "@/components/lost-found/LostFoundModals";
import { useSupabaseAuth } from "@/contexts/SupabaseAuthContext";
import { useNavigate } from "react-router-dom";
import { LostItem } from "@/types";
import { useLostItems } from '@/hooks/useLostItems';
import NewLostItemModal from '@/components/lost-found/NewLostItemModal';

/**
 * Main Lost & Found page component.
 * Handles displaying, filtering, and managing lost items.
 */

const LostFound = () => {
  const page = useLostFoundPage();
  const { logout } = useSupabaseAuth();
  const navigate = useNavigate();
  const { lostItems, isLoading, createLostItem, error, refetchLostItems } = useLostItems();
  
  const handleLogout = () => {
    logout();
    navigate('/auth');
  };

  // Adjust wrapper functions to match expected argument count
  const handleDeliver = (item: LostItem) => {
    if (page.handleDeliver) {
      page.handleDeliver(item, ""); // Only receiver name is needed
    }
  };
  const handleUndo = (item: LostItem) => {
    if (page.handleUndo) {
      page.handleUndo(item);
    }
  };
  const handleEdit = (item: LostItem) => {
    if (page.handleEdit) {
      page.handleEdit(item);
    }
  };
  const handleDelete = (item: LostItem) => {
    if (page.handleDelete) {
      page.handleDelete(item);
    }
  };
  const handleDiscard = (item: LostItem) => {
    if (page.handleDiscard) {
      page.handleDiscard(item);
    }
  };
  const handleConfirmSignature = (receiver: string) => {
    if (page.selectedItem && page.handleConfirmSignature) {
      page.handleConfirmSignature(page.selectedItem, receiver);
    }
  };
  const confirmDelete = () => {
    if (page.selectedItem && page.confirmDelete) {
      page.confirmDelete();
    }
  };
  const confirmDiscard = () => {
    if (page.selectedItem && page.confirmDiscard) {
      page.confirmDiscard();
    }
  };

  // Handler for adding a new lost item (sequential_number is assigned by backend)
  const handleAddLostItem = async (item: { description: string; location: string; photo: string | null; found_by: string; }) => {
    console.log('handleAddLostItem called', item);
    try {
      await createLostItem({
        description: item.description,
        location: item.location,
        photo: item.photo,
        found_by: item.found_by,
      });
      if (page.setNewItemOpen) page.setNewItemOpen(false);
    } catch (error) {
      alert('Failed to create lost item: ' + (error?.message || error));
      console.error('Lost item insert error:', error);
    }
  };

  if (error) {
    return (
      <div className="text-center text-red-500 flex flex-col items-center gap-2 mt-8">
        <span>{error.message}</span>
        <button
          className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
          onClick={() => refetchLostItems()}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <LostFoundHeader
        onBack={() => navigate('/dashboard')}
        username={page.user?.user_metadata?.username || page.user?.email}
        onLogout={handleLogout}
      />
      <LostFoundBody
        searchQuery={page.searchQuery}
        location={page.location}
        status={page.status}
        view={page.view}
        onSearch={page.handleSearch}
        onLocationChange={page.handleLocationChange}
        onStatusChange={page.handleStatusChange}
        onReset={page.handleResetFilters}
        onToggleView={(v) => page.view !== v && page.handleToggleView(v)}
        onNew={() => page.setNewItemOpen && page.setNewItemOpen(true)}
        isMobile={page.isMobile}
        items={lostItems || []}
        handlers={{
          onDeliver: handleDeliver,
          onUndo: handleUndo,
          onEdit: handleEdit,
          onDelete: handleDelete,
          onDiscard: handleDiscard,
          openTimeline: page.openTimeline,
          openInfo: page.handleOpenInfo,
        }}
        setSelectedItem={page.setSelectedItem}
        setDeleteDialogOpen={page.setDeleteDialogOpen}
      />
      <NewLostItemModal
        open={page.newItemOpen || false}
        onOpenChange={page.setNewItemOpen || (() => {})}
        onSubmit={handleAddLostItem}
      />
      <LostFoundModals
        newItemOpen={page.newItemOpen || false}
        setNewItemOpen={page.setNewItemOpen || (() => {})}
        editItemOpen={page.editItemOpen || false}
        setEditItemOpen={page.setEditItemOpen || (() => {})}
        selectedItem={page.selectedItem}
        signatureModalOpen={page.signatureModalOpen || false}
        setSignatureModalOpen={page.setSignatureModalOpen || (() => {})}
        handleConfirmSignature={handleConfirmSignature}
        timelineModalOpen={page.timelineModalOpen || false}
        setTimelineModalOpen={page.setTimelineModalOpen || (() => {})}
        infoModalOpen={page.infoModalOpen || false}
        setInfoModalOpen={page.setInfoModalOpen || (() => {})}
        deleteDialogOpen={page.deleteDialogOpen || false}
        setDeleteDialogOpen={page.setDeleteDialogOpen || (() => {})}
        confirmDelete={confirmDelete}
        discardDialogOpen={page.discardDialogOpen || false}
        setDiscardDialogOpen={page.setDiscardDialogOpen || (() => {})}
        confirmDiscard={confirmDiscard}
      />
    </div>
  );
};

export default LostFound;
